import React from "react";
import { computed } from "mobx";
import { observer } from "mobx-react";
import { format, set } from "date-fns";
import {
  generatePath,
  RouteComponentProps,
  withRouter,
} from "react-router-dom";

import AccessTimeIcon from "@material-ui/icons/AccessTime";

import { Button } from "@material-ui/core";

import PublishAllContentStore from "stores/PublishAllContentStore";
import TodayNutritionStore from "stores/TodayNutritionStore";
import TodayQuickLinksStore from "stores/TodayQuickLinksStore";
import InAppStudioStore from "stores/InAppStudioStore";
import InAppNutritionStore from "stores/InAppNutritionStore";
import InAppResourcesStore from "stores/InAppResourcesStore";
import ContentStudioWorkoutsStore from "stores/ContentStudioWorkoutsStore";
import ContentNutritionRecipesStore from "stores/ContentNutritionRecipesStore";
import ContentResourcesQuickLinksStore from "stores/ContentResourcesQuickLinksStore";
import CategoryDetailsStore from "stores/CategoryDetailsStore";
import NutritionCategoryDetailsStore from "stores/NutritionCategoryDetailsStore";
import ResourcesCategoryDetailsStore from "stores/ResourcesCategoryDetailsStore";

import ConfirmationDialog from "components/ConfirmationDialog";
import Tooltip from "components/Tooltip";
import PublishAllContentForm from "components/PublishAllContentForm";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import ROUTES from "navigation/routes";

import { DEFAULT_TIMEZONE_TEXT } from "constants/dateConstants";

import { Container, TooltipWrapper, IconContainer } from "./styles";
import QuestionnaireQuizStore from "stores/QuestionnaireQuizStore";

type Props = {} & RouteComponentProps & GlobalPopupProps & SnackbarContextProps;

const REGEX_LAST_ROUTE_PARAMETER = /(^.*\/)(.*)$/i;

@observer
class PublishButton extends React.Component<Props> {
  get isExactRoute() {
    const { isExact } = this.props.match;

    return isExact;
  }

  get locationPathname() {
    const { pathname } = this.props.location;

    return pathname;
  }

  get categoryId() {
    const { params } = this.props.match;

    return params.id;
  }

  get contentTabItemId() {
    const matchRes =
      REGEX_LAST_ROUTE_PARAMETER.exec(this.locationPathname) ?? [];

    return matchRes[2];
  }

  get itemId() {
    const { params } = this.props.match;

    return this.isExactRoute
      ? params.workoutId || params.recipeId || params.quickLinkId
      : this.contentTabItemId;
  }

  get afterSubmitCallback() {
    const { path } = this.props.match;

    const pathToCompare = this.isExactRoute ? path : this.locationPathname;

    switch (pathToCompare) {
      case ROUTES.IN_APP_TODAY:
        return () => {};
      case ROUTES.IN_APP_TODAY_NUTRITION:
        return () => {
          TodayNutritionStore.resetPage();
          TodayNutritionStore.fetch();
        };
      case ROUTES.IN_APP_TODAY_QUICK_LINKS:
        return () => {
          TodayQuickLinksStore.resetPage();
          TodayQuickLinksStore.fetch();
        };

      case ROUTES.IN_APP_STUDIO:
        return () => {
          InAppStudioStore.resetPage();
          InAppStudioStore.fetch();
        };
      case ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS:
        return () => {
          CategoryDetailsStore.resetPage();
          Promise.all([
            InAppStudioStore.fetchCategoryDetails(this.categoryId),
            CategoryDetailsStore.fetch(),
          ]);
        };

      case ROUTES.IN_APP_NUTRITION:
        return () => {
          InAppNutritionStore.resetPage();
          InAppNutritionStore.fetch();
        };
      case ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS:
        return () => {
          const isWeekCategory =
            InAppNutritionStore.categoryDetails?.isWeekCategory;

          NutritionCategoryDetailsStore.resetPage();
          Promise.all([
            InAppNutritionStore.fetchCategoryDetails(this.categoryId),
            NutritionCategoryDetailsStore.fetch(),
          ]);
          if (isWeekCategory) {
            NutritionCategoryDetailsStore.fetchAllRecipes();
          }
        };

      case ROUTES.IN_APP_RESOURCES:
        return () => {
          InAppResourcesStore.resetPage();
          InAppResourcesStore.fetch();
        };
      case ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS:
        return () => {
          ResourcesCategoryDetailsStore.resetPage();
          Promise.all([
            InAppResourcesStore.fetchCategoryDetails(this.categoryId),
            ResourcesCategoryDetailsStore.fetch(),
          ]);
        };

      case ROUTES.CONTENT_STUDIO_WORKOUTS:
        return () => {
          ContentStudioWorkoutsStore.resetPage();
          ContentStudioWorkoutsStore.fetch();
        };
      case ROUTES.CONTENT_NUTRITION_RECIPES:
        return () => {
          ContentNutritionRecipesStore.resetPage();
          ContentNutritionRecipesStore.fetch();
        };
      case ROUTES.CONTENT_RESOURCES_QUICK_LINKS:
        return () => {
          ContentResourcesQuickLinksStore.resetPage();
          ContentResourcesQuickLinksStore.fetch();
        };

      case ROUTES.CONTENT_QUESTIONNAIRE_QUIZ:
        return () => {
          QuestionnaireQuizStore.resetPage();
          QuestionnaireQuizStore.fetch();
        };

      case generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
        workoutId: this.itemId,
      }):
      case ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS_WORKOUT_DETAILS:
        return () => {
          ContentStudioWorkoutsStore.fetchWorkoutDetails(this.itemId);
        };
      case generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
        recipeId: this.itemId,
      }):
      case ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS_RECIPE_DETAILS:
        return () => {
          ContentNutritionRecipesStore.fetchRecipeDetails(this.itemId);
        };
      case generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
        quickLinkId: this.itemId,
      }):
      case ROUTES.IN_APP_TODAY_NUTRITION_QUICK_LINK_DETAILS:
      case ROUTES.IN_APP_TODAY_QUICK_LINKS_LINK_DETAILS:
      case ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS_QUICK_LINK_DETAILS:
        return () => {
          ContentResourcesQuickLinksStore.fetchQuickLinkDetails(this.itemId);
        };
    }
  }

  @computed get scheduleInfoText() {
    const scheduleDate = PublishAllContentStore.scheduleDate;
    return scheduleDate
      ? `Content update in production is scheduled for ${format(
          new Date(scheduleDate),
          "PPP hh:mm a",
        )} ${DEFAULT_TIMEZONE_TEXT}.`
      : "";
  }

  async componentDidMount() {
    await PublishAllContentStore.fetchScheduleDate();
  }

  onPublishAllContent = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "PublishAllContent",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <PublishAllContentForm
          onClose={close}
          onSubmit={this.publishAllContent(close)}
          isLoading={PublishAllContentStore.isLoading}
          scheduleInfoText={this.scheduleInfoText}
        />
      ),
    });
  };

  publishAllContent = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { isScheduleLater, scheduleDate, scheduleTimeMinutes } = values;

    try {
      await PublishAllContentStore.publishAllContent(
        isScheduleLater
          ? set(scheduleDate, { minutes: scheduleTimeMinutes })
          : undefined,
      );

      close();
      PublishAllContentStore.fetchScheduleDate();
      this.afterSubmitCallback && this.afterSubmitCallback();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onShowScheduleInfo = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "ShowScheduleInfo",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Update Scheduled"
          text={this.scheduleInfoText}
          declineText="Cancel"
          acceptText="Discard"
          onDecline={close}
          onAccept={this.cancelPublishing(close)}
          isLoading={PublishAllContentStore.isLoading}
        />
      ),
    });
  };

  cancelPublishing = (close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await PublishAllContentStore.cancelPublishing();

      close();
      PublishAllContentStore.fetchScheduleDate();
      this.afterSubmitCallback && this.afterSubmitCallback();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Container>
        {this.scheduleInfoText && (
          <TooltipWrapper>
            <Tooltip content={this.scheduleInfoText}>
              <IconContainer onClick={this.onShowScheduleInfo}>
                <AccessTimeIcon />
              </IconContainer>
            </Tooltip>
          </TooltipWrapper>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={this.onPublishAllContent}
        >
          Publish all content
        </Button>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(PublishButton)));
